import { Outlet, data, useLoaderData, useLocation } from "react-router";
import { getBlog } from "app/.server/blogs";
import { getMeta } from "app/utils/meta";
import ProgressBar from "app/components/blogs/progress-bar";
import ShareButtons from "app/components/blogs/share-buttons";
import Link from "app/components/i18n/Link";
import Layout from "app/components/layout";
import Sprite from "app/components/sprite";
import { ZBNI_WEBSITE_URL } from "app/config/constants";
import formatDate from "app/utils/date";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * @param {import('react-router').LoaderFunctionArgs}
 * @returns
 */
export const loader = async ({ request }) => {
  const url = new URL(request.url);

  const { frontmatter, otherPosts } = await getBlog(url.pathname);

  if (!frontmatter.hasDetailPage) {
    throw data("Not found", { status: 404, statusText: "Not Found" });
  }

  return { frontmatter, otherPosts };
};

export const meta = ({ loaderData: data }) => {
  return getMeta({
    title: `${data.frontmatter.title} | ${data.frontmatter.meta_description}`,
    description: data.frontmatter.description,
  });
};

const BlogDetail = () => {
  const blog = useLoaderData();
  const showTags = false;
  const { frontmatter, otherPosts } = blog;
  const location = useLocation();

  const [showHeader, setShowHeader] = useState(false);
  const [isTop, setIsTop] = useState(false);
  const imageRef = useRef();
  const shareSectionRef = useRef();
  const progressBar = useRef();
  const blogHeader = useRef();
  const sideArticles = useRef();
  const [scrollY, setScrollY] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  const encodedSlug = encodeURIComponent(
    `${ZBNI_WEBSITE_URL}${location.pathname}`
  );
  const encodedTitle = encodeURIComponent(frontmatter.title);

  const setProgress = () => {
    const winScroll =
      document.body.scrollTop || document.documentElement.scrollTop;
    const height =
      document.documentElement.scrollHeight -
      document.documentElement.clientHeight -
      footerHeight;
    const scrolled = (winScroll / height) * 100;
    progressBar.current.style.width = scrolled + "%";
  };

  useLayoutEffect(() => {
    const headerHeight = blogHeader.current.offsetHeight * 2;
    sideArticles.current.style.top = headerHeight + "px";
  }, [showHeader]);

  useLayoutEffect(() => {
    const footer = document.querySelector("footer");
    const onResize = () => {
      setFooterHeight(footer.clientHeight);
    };

    setFooterHeight(footer.clientHeight);

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [footerHeight]);

  useEffect(() => {
    const onScrollFunc = () => {
      setScrollY(() => window.scrollY);
      if (imageRef.current) {
        setShowHeader(window.scrollY >= imageRef.current.offsetTop);
      }
      if (shareSectionRef.current) {
        setIsTop(
          window.scrollY >= shareSectionRef.current.getBoundingClientRect().top
        );
      }
      setProgress();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("scroll", onScrollFunc);
    }

    return () => {
      window.removeEventListener("scroll", onScrollFunc);
    };
  }, [scrollY]);

  return (
    <Layout headerIsOnTop={false} enableHeaderShrinkingOnScroll={false}>
      {/* <Seo
        title={frontmatter.title}
        description={frontmatter.meta_description}
        lang={language}
      /> */}
      <div className="relative h-full bg-white font-montserrat">
        <div
          className="absolute hidden h-[340px]
          w-full bg-secondary-lightest lg:block"
        />
        <ProgressBar reference={progressBar} />
        {/* STICKY HEADER */}
        <div
          className={`fixed z-[2] min-h-fit w-full bg-white shadow-lg ${
            showHeader ? "top-[88px] lg:top-[72px]" : "invisible -top-20"
          } hidden transition-all duration-500 lg:block`}
        >
          <div ref={blogHeader} className="zbooni-layout flex py-2">
            <div className="flex-1 pl-10 pr-5">
              <div className="flex-col px-10 py-4 lg:flex">
                <p className="text-xl font-extrabold leading-8">
                  {frontmatter.title}
                </p>
                <div className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-ghostwhite p-1">
                    <Sprite name="blog-author" className="h-full w-full" />
                  </div>
                  <p
                    className="text-xs font-medium leading-6
                    text-secondary ltr:ml-2 rtl:mr-2"
                  >
                    Written by {frontmatter.author} |{" "}
                    {formatDate(frontmatter.date)}
                  </p>
                </div>
              </div>
            </div>
            <div
              className="flex items-center gap-x-3
              lg:w-4/12 xl:w-3/12 xl:gap-x-4"
            >
              <ShareButtons
                withLabel={false}
                slug={encodedSlug}
                title={encodedTitle}
              />
            </div>
          </div>
        </div>
        {/* END STICKY HEADER */}
        <div className="zbooni-layout relative pt-[84px]">
          <div className="mt-4 flex h-full w-full flex-col lg:mt-10 lg:flex-row">
            <div
              className="flex-1 ltr:lg:pl-0 ltr:lg:pr-10
              rtl:lg:pl-10 rtl:lg:pr-0 ltr:xl:pl-10 rtl:xl:pr-10"
            >
              <div
                className="relative bg-white py-5 lg:mb-16
                lg:rounded-2xl lg:px-10 lg:py-8 lg:shadow-2xl"
              >
                <p className="text-[28px] font-extrabold leading-8">
                  {frontmatter.title}
                </p>
                <div className="mt-2 flex items-center">
                  <div className="h-7 w-7 rounded-full bg-ghostwhite p-1">
                    <Sprite name="blog-author" className="h-full w-full" />
                  </div>
                  <p className="ml-2 text-[10px] font-medium leading-6">
                    Written by {frontmatter.author} |{" "}
                    {formatDate(frontmatter.date)}
                  </p>
                </div>
                <div
                  className={`sticky top-[72px] z-10 -mx-7 ${
                    isTop ? "shadow-lg" : ""
                  } block bg-white lg:hidden`}
                >
                  <ShareButtons
                    withLabel={true}
                    sectionRef={shareSectionRef}
                    slug={encodedSlug}
                    title={encodedTitle}
                  />
                </div>
                {frontmatter.image && (
                  <div ref={imageRef} className="mt-5">
                    <img
                      src={frontmatter.image}
                      alt={frontmatter.title}
                      className="h-full w-full rounded-2xl object-cover"
                    />
                  </div>
                )}
                <article id="blog-content-wrapper" className="mt-5">
                  <Outlet />
                  {showTags && (
                    <div
                      className="flex flex-wrap gap-2 py-3
                    text-[10px] font-extrabold uppercase
                    tracking-wider text-secondary lg:pb-0"
                    >
                      {frontmatter.tag.map((tag, index) => (
                        <span
                          key={index}
                          className="break-all rounded-full bg-ghostwhite
                        px-3 text-[10px] font-extrabold uppercase
                        tracking-wider text-secondary"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              </div>
            </div>
            <div className="flex flex-col lg:w-3/12">
              <div className="hidden lg:block">
                <ShareButtons
                  withLabel={true}
                  slug={encodedSlug}
                  title={encodedTitle}
                />
              </div>
              <div ref={sideArticles} className="mb-16 lg:sticky lg:self-start">
                <p className="mb-4 pt-7 font-extrabold text-secondary-dark">
                  Other articles
                </p>
                <div
                  className="max-w-[270px] rounded-2xl bg-white p-4
                  text-xs font-semibold text-blue shadow-md"
                >
                  {otherPosts.map((post, index) => {
                    return (
                      <Link
                        key={index}
                        to={post.slug}
                        rel="prev"
                        className={`my-4 block ${
                          index == 1 && "border-y border-dotted py-4"
                        }`}
                      >
                        {post.frontmatter.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BlogDetail;
